/* eslint-disable better-mutation/no-mutation */
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
    filterSelectableSuggestions,
    filterCombinedSelectableSuggestions
} from './attributes-helpers';
import type { SyiAttribute } from '../../components/attributes/types';

type CategorySuggestedAttribute = {
    id: number;
    name: string;
    values: string[];
};

type TermAttributeValue = {
    id: number;
    backingField: string;
    userGenerated: boolean;
    values: string[];
};

type InitialState = {
    categoryAttributes: SyiAttribute[];
    draftAttributes:
        | Record<string, never>
        | {
              termAttributeValues: TermAttributeValue[];
              numberAttributeValues: [];
          };
    selectableSuggestedAttributes: CategorySuggestedAttribute[];
    selectableCategorySuggestedAttributes: SyiAttribute[];
    selectableImageToColorSuggestedAttributes: SyiAttribute[];
    imageToColorSuggestedAttributes: CategorySuggestedAttribute[];
    validationErrors: Record<string, string>;
    categorySuggestedAttributes: CategorySuggestedAttribute[];
    suggestionsExtractorType: string;
};

const initialState: InitialState = {
    // The attributes for the current category
    categoryAttributes: [],
    // The attributes of the draft.
    /**
     draftAttributes: {
      termAttributeValues: [
        {
          id: 1,
          backingField: 'clothes_size',
          userGenerated: true,
          values: [
            'b'
          ]
        }
      ],
      numberAttributeValues: []
    }
     */
    draftAttributes: {},
    // A list of the selected attributes (draftAttributes) for display on the page.
    // Exists for ease of use in views.
    // Format:
    /**
     {
         title: 'Klädstorlek',
         value: 'L'
     }
     */
    selectableSuggestedAttributes: [],
    selectableCategorySuggestedAttributes: [],
    selectableImageToColorSuggestedAttributes: [],
    // Attribute suggestions from the image-to-color service.
    imageToColorSuggestedAttributes: [],
    validationErrors: {},
    categorySuggestedAttributes: [],
    suggestionsExtractorType: ''
};

const updateSelectableImageToColorSuggestedAttributes = (
    state: InitialState
) => {
    state.selectableImageToColorSuggestedAttributes =
        filterSelectableSuggestions(
            state.imageToColorSuggestedAttributes,
            state.categoryAttributes
        );
    updateSelectableSuggestedAttributes(state);
};

const updateSelectableSuggestedAttributes = (state: InitialState) => {
    state.selectableSuggestedAttributes = filterCombinedSelectableSuggestions(
        state.selectableCategorySuggestedAttributes,
        state.selectableImageToColorSuggestedAttributes,
        state.categoryAttributes
    );
};

const attributeSlice = createSlice({
    name: 'attributes',
    initialState,
    reducers: {
        setCategoryAttributesSuggestions: (
            state,
            action: PayloadAction<{
                suggestedAttributes: CategorySuggestedAttribute[];
                suggestionsExtractorType: string;
            }>
        ) => {
            const { payload } = action;
            state.categorySuggestedAttributes = payload.suggestedAttributes;
            state.selectableCategorySuggestedAttributes =
                filterSelectableSuggestions(
                    payload.suggestedAttributes,
                    state.categoryAttributes
                );
            state.suggestionsExtractorType = payload.suggestionsExtractorType;
            updateSelectableSuggestedAttributes(state);
            state.validationErrors = validate(state);
        },
        setImageToColorSuggestions: (
            state,
            action: PayloadAction<{
                suggestedAttributes: CategorySuggestedAttribute[];
            }>
        ) => {
            const { payload } = action;
            state.imageToColorSuggestedAttributes = payload.suggestedAttributes;
            updateSelectableImageToColorSuggestedAttributes(state);
            state.validationErrors = validate(state);
        },

        setCategoryAttributeOption: (
            state,
            action: PayloadAction<{
                attributeId: number;
                optionValue: string;
                activate: boolean;
            }>
        ) => {
            const { payload } = action;
            const attribute = state.categoryAttributes.find(
                (a) => a.id === payload.attributeId
            );
            if (attribute) {
                let options = attribute.options;
                if (
                    !attribute.isMultiSelect ||
                    (!payload.optionValue && !payload.activate)
                ) {
                    options = options.map((o) =>
                        !o.isActive ? o : { ...o, isActive: false }
                    );
                }
                if (payload.optionValue) {
                    options = options.map((o) =>
                        o.value !== payload.optionValue
                            ? o
                            : { ...o, isActive: payload.activate }
                    );
                }
                const numberOfValuesSelected = options.filter(
                    (o) => o.isActive
                ).length;
                attribute.options = options;
                attribute.numberOfValuesSelected = numberOfValuesSelected;
            }
            state.validationErrors = validate(state);
        },
        updateCategoryAttributesOptionsStatuses: (state) => {
            state.categoryAttributes.forEach((a) => {
                const draftAttributeValues =
                    state.draftAttributes.termAttributeValues?.find(
                        (tav) => a.id === tav.id
                    )?.values || [];
                a.options?.forEach((o) => {
                    const isActive = draftAttributeValues.includes(o.value);
                    o.isActive = isActive;
                });
                a.numberOfValuesSelected = a.options?.filter(
                    (o) => o.isActive
                ).length;
            });
            state.validationErrors = validate(state);
        },
        setDraftAttributes: (state, action) => {
            state.draftAttributes = { ...action.payload };
            state.validationErrors = validate(state);
        },
        setCategoryAttributes: (state, action) => {
            state.categoryAttributes = [...action.payload];
            updateSelectableImageToColorSuggestedAttributes(state);
            state.validationErrors = validate(state);
        },
        resetAttributes: () => initialState,

        resetImageAttributeSuggestions: (state) => {
            state.imageToColorSuggestedAttributes =
                initialState.imageToColorSuggestedAttributes;
            updateSelectableImageToColorSuggestedAttributes(state);
            state.validationErrors = validate(state);
        }
    }
});

const validate = (state: InitialState) => {
    const validationErrors: Record<string, string> = {};
    state.categoryAttributes.forEach((attribute) => {
        if (attribute.minNumberOfValues === 0) {
            return;
        }
        if (attribute.options?.length === 0) {
            // There were no options to choose from.
            // May not happen when backend implementation is done,
            // we should not get an empty list of options.
            return;
        }
        const values = attribute.options?.reduce((acc, option) => {
            if (option.isActive) {
                acc.push(option.value);
            }
            return acc;
        }, [] as string[]);
        if (values?.length === 0 && attribute.backingField) {
            validationErrors[attribute.backingField] =
                `attributes:::syi_validation_missing_attribute_${attribute.backingField}`;
        }
    });
    return validationErrors;
};

const { actions, reducer } = attributeSlice;
export const {
    setCategoryAttributesSuggestions,
    setImageToColorSuggestions,
    setCategoryAttributeOption,
    updateCategoryAttributesOptionsStatuses,
    setDraftAttributes,
    setCategoryAttributes,
    resetAttributes,
    resetImageAttributeSuggestions
} = actions;

export default reducer;
